import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../atoms/Button';
import { Input } from '../atoms/Input';
import { clearAllData, clearLocalStorageOnly, clearServerDataOnly, downloadEncryptedData, importDataFromFile } from '../../utils/dataManagement';
import { parsePayoutsFromCSV, exportPayoutsImportable } from '../../utils/csv';
import { loadPayoutsFromStorage } from '../../utils/storage';
import { usePayoutMutations } from '../../utils/hooks/usePayoutMutations';
import type { CsvImportResult } from '../../utils/csv';

interface DataManagementSectionProps {
  showDataManagement: boolean;
}

interface ActionMessage {
  type: 'success' | 'error';
  text: string;
}

export const DataManagementSection: React.FC<DataManagementSectionProps> = ({ showDataManagement }) => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const { addPayout } = usePayoutMutations();

  const [exportPassword, setExportPassword] = useState('');
  const [importPassword, setImportPassword] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);

  // CSV import state
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<CsvImportResult | null>(null);
  const [isCsvParsing, setIsCsvParsing] = useState(false);
  const [isCsvImporting, setIsCsvImporting] = useState(false);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setActionMessage({ type, text });
    setTimeout(() => setActionMessage(null), 3000);
  };

  const handleClearData = async (type: 'all' | 'server' | 'local') => {
    let confirmMessage = '';
    let successMessage = '';
    let clearFunction: () => Promise<void>;

    switch (type) {
      case 'all':
        confirmMessage = 'Êtes-vous sûr de vouloir effacer TOUTES les données (serveur + stockage local) ? Cette action est irréversible.';
        successMessage = 'Toutes les données supprimées avec succès';
        clearFunction = clearAllData;
        break;
      case 'server':
        confirmMessage = 'Êtes-vous sûr de vouloir effacer les données du serveur uniquement ? Cette action est irréversible.';
        successMessage = 'Données du serveur supprimées avec succès';
        clearFunction = clearServerDataOnly;
        break;
      case 'local':
        confirmMessage = 'Êtes-vous sûr de vouloir effacer les données du stockage local uniquement ? Cette action est irréversible.';
        successMessage = 'Données locales supprimées avec succès';
        clearFunction = clearLocalStorageOnly;
        break;
    }

    if (window.confirm(confirmMessage)) {
      try {
        await clearFunction();
        showMessage('success', successMessage);
        // Recharger la page pour réinitialiser l'état
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } catch (error) {
        showMessage('error', error instanceof Error ? error.message : 'Erreur lors de la suppression des données');
      }
    }
  };

  const handleExportData = async () => {
    if (!exportPassword) {
      showMessage('error', 'Veuillez saisir un mot de passe pour l\'export');
      return;
    }

    try {
      await downloadEncryptedData(exportPassword);
      showMessage('success', 'Export réussi ! Fichier téléchargé.');
      setExportPassword('');
    } catch {
      showMessage('error', 'Erreur lors de l\'export des données');
    }
  };

  const handleImportData = async () => {
    if (!importFile || !importPassword) {
      showMessage('error', 'Veuillez sélectionner un fichier et saisir le mot de passe');
      return;
    }

    try {
      const result = await importDataFromFile(importFile, importPassword, { replaceExisting: true });

      if (isAuthenticated && result) {
        // Utilisateur connecté : données synchronisées avec le serveur
        showMessage(
          'success',
          `Import réussi ! ${result.imported} transaction(s) importée(s). Rechargement...`
        );

        // Invalider le cache pour recharger les payouts depuis le serveur
        queryClient.invalidateQueries({ queryKey: ['payouts'] });

        setImportFile(null);
        setImportPassword('');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        // Utilisateur invité : données stockées localement uniquement
        showMessage('success', 'Import réussi ! Rechargement de la page...');
        setImportFile(null);
        setImportPassword('');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      showMessage('error', error instanceof Error ? error.message : 'Erreur lors de l\'import');
    }
  };

  const handleCsvFileChange = async (file: File | null) => {
    setCsvFile(file);
    setCsvPreview(null);
    if (!file) return;

    setIsCsvParsing(true);
    try {
      const text = await file.text();
      const existing = await loadPayoutsFromStorage();
      const result = parsePayoutsFromCSV(text, existing);
      setCsvPreview(result);
    } catch (error) {
      showMessage('error', error instanceof Error ? error.message : 'Fichier CSV invalide');
    } finally {
      setIsCsvParsing(false);
    }
  };

  const handleCsvImport = async () => {
    if (!csvPreview || csvPreview.toImport.length === 0) return;
    setIsCsvImporting(true);
    try {
      await Promise.all(csvPreview.toImport.map(p => addPayout(p)));
      queryClient.invalidateQueries({ queryKey: ['payouts'] });
      showMessage('success', `${csvPreview.toImport.length} transaction(s) importée(s) avec succès.`);
      setCsvFile(null);
      setCsvPreview(null);
    } catch (error) {
      showMessage('error', error instanceof Error ? error.message : "Erreur lors de l'import CSV");
    } finally {
      setIsCsvImporting(false);
    }
  };

  const handleCsvExport = async () => {
    try {
      const payouts = await loadPayoutsFromStorage();
      exportPayoutsImportable(payouts);
    } catch (error) {
      showMessage('error', "Erreur lors de l'export CSV");
    }
  };

  if (!showDataManagement) return null;

  return (
    <div className="space-y-6">
      {/* Action Messages */}
      {actionMessage && (
        <div
          className={`p-3 rounded-lg text-sm ${
            actionMessage.type === 'success'
              ? 'bg-green-100 text-green-800 border border-green-200'
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}
        >
          {actionMessage.text}
        </div>
      )}

      {/* Export Data */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
          <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          Exporter les données (chiffrées)
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          {isAuthenticated
            ? 'Exporte toutes vos données depuis le serveur dans un fichier chiffré pour sauvegarde ou transfert.'
            : 'Exporte toutes vos données depuis le stockage local dans un fichier chiffré pour sauvegarde ou transfert.'}
        </p>
        <div className="flex gap-3">
          <Input
            type="password"
            placeholder="Mot de passe pour le chiffrement"
            value={exportPassword}
            onChange={(e) => setExportPassword(e.target.value)}
            className="flex-1"
          />
          <Button
            onClick={handleExportData}
            disabled={!exportPassword}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Exporter
          </Button>
        </div>
      </div>

      {/* Import Data */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
          <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
            />
          </svg>
          Importer des données chiffrées
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          {isAuthenticated
            ? 'Importe des données depuis un fichier de sauvegarde chiffré vers le serveur. Les données seront synchronisées avec votre compte.'
            : 'Importe des données depuis un fichier de sauvegarde chiffré vers le stockage local.'}{' '}
          <strong>Attention :</strong> cela remplacera toutes vos données actuelles.
        </p>
        <div className="space-y-3">
          <input
            type="file"
            accept=".enc,.txt"
            onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <div className="flex gap-3">
            <Input
              type="password"
              placeholder="Mot de passe de déchiffrement"
              value={importPassword}
              onChange={(e) => setImportPassword(e.target.value)}
              className="flex-1"
            />
            <Button
              onClick={handleImportData}
              disabled={!importFile || !importPassword}
              className="bg-green-600 hover:bg-green-700"
            >
              Importer
            </Button>
          </div>
        </div>
      </div>

      {/* Export / Import CSV */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
          <svg className="w-4 h-4 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export / Import CSV
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Format CSV natif réimportable — contient tous les champs, compatible entre comptes et instances.
        </p>

        {/* Export */}
        <div className="mb-4">
          <Button onClick={handleCsvExport} className="bg-emerald-600 hover:bg-emerald-700">
            Télécharger tous les payouts (CSV)
          </Button>
        </div>

        {/* Import */}
        <div className="space-y-3">
          <input
            type="file"
            accept=".csv"
            onChange={(e) => handleCsvFileChange(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
          />

          {isCsvParsing && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Analyse du fichier...</p>
          )}

          {csvPreview && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-1 text-sm">
              <p className="text-green-700 font-medium">
                {csvPreview.toImport.length} transaction(s) à importer
              </p>
              {csvPreview.duplicates.length > 0 && (
                <p className="text-amber-700">
                  {csvPreview.duplicates.length} doublon(s) ignoré(s)
                </p>
              )}
              {csvPreview.invalid > 0 && (
                <p className="text-red-700">
                  {csvPreview.invalid} ligne(s) invalide(s) ignorée(s)
                </p>
              )}
              <Button
                onClick={handleCsvImport}
                disabled={csvPreview.toImport.length === 0 || isCsvImporting}
                className="mt-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
              >
                {isCsvImporting
                  ? 'Import en cours...'
                  : `Importer ${csvPreview.toImport.length} transaction(s)`}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Clear Data */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-red-200">
        <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center">
          <svg className="w-4 h-4 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
          Effacer les données
        </h4>

        {isAuthenticated ? (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              En tant qu'utilisateur connecté, vous pouvez choisir de supprimer vos données du serveur, du stockage local, ou les deux. <strong>Cette action est irréversible.</strong>
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => handleClearData('all')}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                Effacer toutes les données (serveur + local)
              </Button>
              <Button
                onClick={() => handleClearData('server')}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white"
              >
                Effacer uniquement les données du serveur
              </Button>
              <Button
                onClick={() => handleClearData('local')}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                Effacer uniquement le stockage local
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Supprime définitivement toutes les données stockées localement. <strong>Cette action est irréversible.</strong>
            </p>
            <Button
              onClick={() => handleClearData('local')}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Effacer toutes les données
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
